<script setup lang="ts">
  import Badge from '../ui/badge/Badge.vue'
  import { useRouter } from 'vue-router'
  import { computed, inject, type Ref } from 'vue'
  import type { ClientCase, ClientQuestionnaireSubmission, ClientRequest } from '@/types/generated'
  import ClientPortalCompleteButton from '@/components/clientPortal/clientPortalCompleteButton.vue'
  import ClientPortalDownloadButton from '@/components/clientPortal/clientPortalDownloadButton.vue'
  import { CalendarIcon, ListTodo, FileText } from 'lucide-vue-next'
  import { ROUTES } from '@/router/routes'
  import { useTranslation } from '@/composables/useTranslation'
  import { useThemeColor } from '@/composables/useThemeColorV3'

  const router = useRouter()
  const caseRecord = inject<Ref<ClientCase | null>>('caseRecord')
  const accessToken = inject<Ref<string | null>>('accessToken')
  const themeColor = computed(() => useThemeColor().value)
  const { t } = useTranslation({
    en: {
      dueBy: 'Due by',
      questionsAnswered: 'questions answered',
      completed: 'Completed',
      pending: 'Pending',
      pleaseFillOutDocument: 'Please fill out the document'
    },
    de: {
      dueBy: 'Fällig bis',
      questionsAnswered: 'Fragen beantwortet',
      completed: 'Abgeschlossen',
      pending: 'Ausstehend',
      pleaseFillOutDocument: 'Bitte füllen Sie das Dokument aus'
    }
  })

  const formatDate = (dateString: string | number | Date) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date)
  }

  type ActionItem = ClientQuestionnaireSubmission | ClientRequest

  function isQuestionnaireSubmission(item: ActionItem): item is ClientQuestionnaireSubmission {
    return 'state' in item && 'title' in item && 'question_count' in item
  }

  function isFormRequest(item: ActionItem): item is ClientRequest {
    return 'form' in item && 'token' in item
  }

  function isSubmitted(item: ActionItem): boolean {
    if (isQuestionnaireSubmission(item)) {
      return item.state === 'CLOSED'
    } else {
      return hasResponse(item)
    }
  }

  function hasResponse(request: ClientRequest): boolean {
    return request.response_present as unknown as boolean
  }

  function getTitle(item: ActionItem): string {
    if (isQuestionnaireSubmission(item)) {
      return item.title
    } else {
      return item.form?.title || item.form?.document_name || ''
    }
  }

  function getDueDate(item: ActionItem): string | number | Date {
    if (isQuestionnaireSubmission(item)) {
      if (item.state === 'CLOSED') {
        return ''
      }
      return item.due_at
    } else {
      if (hasResponse(item)) {
        return ''
      }
      return item.form.due_at
    }
  }

  function getProgressText(item: ActionItem, showFullText: boolean = true): string {
    if (isQuestionnaireSubmission(item)) {
      if (item.state === 'CLOSED') {
        return ''
      }
      const numberPart = `${item.answered_question_count}/${item.question_count}`
      return showFullText ? `${numberPart} ${t('questionsAnswered')}` : numberPart
    } else {
      if (hasResponse(item)) {
        return ''
      }
      return t('pleaseFillOutDocument')
    }
  }

  function getProgressPercentage(item: ActionItem): number {
    if (isQuestionnaireSubmission(item)) {
      const percentage = Math.round((item.answered_question_count / item.question_count) * 100)
      return percentage
    } else {
      const percentage = hasResponse(item) ? 100 : 0
      console.log('PDF progress percentage:', percentage)
      return percentage
    }
  }

  function getBadgeText(item: ActionItem): string {
    if (isQuestionnaireSubmission(item)) {
      if (item.state === 'CLOSED') return t('completed')
      return t('pending')
    } else {
      return hasResponse(item) ? t('completed') : t('pending')
    }
  }

  function getBadgeClass(item: ActionItem): string {
    if (isQuestionnaireSubmission(item)) {
      if (item.state === 'CLOSED') {
        return 'bg-teal-100 text-teal-700'
      }
      return `bg-${themeColor.value}-100 text-${themeColor.value}-700`
    } else {
      return hasResponse(item)
        ? 'bg-teal-100 text-teal-700'
        : `bg-${themeColor.value}-100 text-${themeColor.value}-700`
    }
  }

  function getStatusPriority(item: ActionItem): number {
    if (isQuestionnaireSubmission(item)) {
      switch (item.state) {
        case 'RECEIVED':
          return 1
        case 'OPEN':
          return 2
        case 'CLOSED':
          return 3
        default:
          return 4
      }
    } else if (isFormRequest(item)) {
      return hasResponse(item) ? 3 : 1
    }
    return 4
  }

  const combinedItems = computed(() => {
    const submissions: ClientQuestionnaireSubmission[] =
      caseRecord?.value?.questionnaire_submissions.filter(
        (submission) => !submission.is_third_party_questionnaire
      ) || []
    const requests: ClientRequest[] =
      caseRecord?.value?.form_requests.filter((request) => !request.is_third_party) || []
    return [...submissions, ...requests].sort((a, b) => getStatusPriority(a) - getStatusPriority(b))
  })

  function navigateToQuestionaire(submissionId: any) {
    router.push({
      path: `${ROUTES.CLIENT_PORTAL.QUESTIONNAIRE_DETAIL.replace(':uuid', '')}${submissionId}`,
      query: { access_token: accessToken?.value || '' }
    })
  }

  function navigateToDocument(token: string) {
    router.push({
      path: `${ROUTES.CLIENT_PORTAL.PDF_TEMPLATE_DETAIL.replace(':form_token', '')}${token}`,
      query: { access_token: accessToken?.value || '' }
    })
  }

  const buttonType = (item: ActionItem) => {
    if (isFormRequest(item)) {
      if (hasResponse(item)) {
        return {
          component: ClientPortalDownloadButton,
          props: {
            pdfUrl: item.form.file || '',
            filename: item.form.document_name || 'document.pdf',
            requestToken: item.token
          }
        }
      } else {
        return {
          component: ClientPortalCompleteButton,
          onClick: () => navigateToDocument(item.token)
        }
      }
    } else if (isQuestionnaireSubmission(item)) {
      if (item.state === 'CLOSED') {
        const downloadUrl = item.pdf_url || ''
        return {
          component: ClientPortalDownloadButton,
          props: {
            pdfUrl: downloadUrl,
            filename: `${item.title}.pdf`,
            documentUuid: item.uuid
          }
        }
      } else {
        const onClick = () => navigateToQuestionaire(item.short_id)
        return { component: ClientPortalCompleteButton, onClick }
      }
    }
    return null
  }
</script>

<template>
  <div
    data-cy="ClientPortalItemsV3"
    class="space-y-4"
  >
    <div
      v-for="item in combinedItems"
      :key="isQuestionnaireSubmission(item) ? item.uuid : item.id"
      :data-cy="
        isQuestionnaireSubmission(item)
          ? 'ClientPortalItemQuestionnaire'
          : 'ClientPortalItemPdfTemplate'
      "
      class="rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
    >
      <div class="flex flex-col gap-4 md:flex-row md:items-center">
        <div
          class="flex flex-shrink-0 items-center gap-3 md:items-center md:overflow-hidden"
          :class="isSubmitted(item) ? 'md:max-w-[70%]' : 'md:max-w-[45%]'"
        >
          <div class="rounded-full bg-gray-100 p-2.5">
            <ListTodo
              v-if="isQuestionnaireSubmission(item)"
              class="h-5 w-5 text-gray-700 md:h-7 md:w-7"
            />
            <FileText
              v-else
              class="h-5 w-5 text-gray-700 md:h-7 md:w-7"
            />
          </div>

          <div class="flex-1 space-y-2 overflow-hidden">
            <div class="flex items-center justify-between gap-2 md:items-center md:justify-start">
              <h3
                data-cy="ClientPortalItemTitle"
                class="truncate text-sm font-medium leading-5 text-gray-900 md:text-base md:leading-normal"
              >
                {{ getTitle(item) }}
              </h3>
              <Badge
                data-cy="ClientPortalItemBadge"
                :class="getBadgeClass(item)"
                variant="default"
                class="flex-shrink-0 px-1 py-0.5 text-xs md:px-3 md:py-1 md:text-sm"
              >
                {{ getBadgeText(item) }}
              </Badge>
            </div>

            <div
              v-if="getDueDate(item)"
              class="flex items-center gap-1 whitespace-nowrap text-xs text-gray-600 md:text-sm"
            >
              <CalendarIcon class="h-4 w-4 md:h-5 md:w-5" />
              <span>{{ t('dueBy') }} {{ formatDate(getDueDate(item)) }}</span>
            </div>
          </div>
        </div>

        <div
          class="flex flex-col gap-3 md:flex-1 md:flex-row md:items-center md:justify-end md:gap-6"
        >
          <div class="flex items-center gap-2">
            <div
              v-if="getProgressText(item)"
              class="flex items-center gap-2 text-xs text-gray-600 md:border-l md:border-gray-200 md:pl-4 md:text-sm"
            >
              <span class="inline md:hidden lg:inline">{{ getProgressText(item, true) }}</span>
              <span class="hidden md:inline lg:hidden">{{ getProgressText(item, false) }}</span>
            </div>

            <div
              v-if="isQuestionnaireSubmission(item) && item.state !== 'CLOSED'"
              class="min-w-[70px] max-w-[200px] flex-1 md:max-w-[145px]"
            >
              <div class="h-2 w-full overflow-hidden rounded-full bg-gray-200 md:h-2.5">
                <div
                  class="h-full rounded-full transition-all duration-300"
                  :class="`bg-${themeColor}-600`"
                  :style="{ width: Math.max(getProgressPercentage(item), 5) + '%' }"
                ></div>
              </div>
            </div>
          </div>

          <div class="flex-shrink-0 md:mr-2">
            <component
              :is="buttonType(item)?.component"
              data-cy="ClientPortalItemActionButton"
              v-bind="{ ...buttonType(item)?.props, class: 'w-full md:w-auto' }"
              @click="buttonType(item)?.onClick"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
